/**
 * One customer, in four tabs, with the credit standing beside all of them.
 *
 * **The credit panel does not move.** An operator changing somebody's role
 * still needs to see that the company is over its limit — that is often *why*
 * they are on the screen — so it sits outside the tabs rather than inside one
 * of them.
 *
 * **The active tab is a URL parameter, not component state.** A link to "this
 * customer's tariff conditions" has to be a link, and the browser's back
 * button has to undo a tab change like everything else.
 *
 * **Withdrawing access is a separate action with a separate permission.** It
 * cuts every live session immediately; folding it into the identity form would
 * mean anybody who can fix a billing contact can also cut a company off the
 * portal.
 */

import { useCallback, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { useCan } from "@hardauto/core-access";
import type { ApiClient } from "@hardauto/core-api";
import { useAction, useResource } from "@hardauto/core-data";
import { Field } from "@hardauto/core-forms";
import { day, moment, money, percent } from "@hardauto/core-i18n";
import {
  Button,
  Card,
  Checkbox,
  DataTable,
  DefinitionList,
  Dialog,
  ErrorState,
  Input,
  LoadingState,
  Meter,
  Page,
  Select,
  StatusDot,
  Tabs,
  Textarea,
  useToast,
  type Column,
} from "@hardauto/core-ui";

import {
  ClientsApi,
  type Client,
  type ClientUser,
  type ClientUserRole,
  type ClientUserState,
} from "../api";
import { creditTone } from "../credit";
import { NAMESPACE } from "../i18n";
import { CLIENTS_PATHS } from "../paths";
import type { LoadTariff, TariffLine } from "../tariff";
import "./clients.css";

type TabId = "users" | "identity" | "terms" | "scope" | "notes";
const TAB_IDS: readonly TabId[] = ["users", "identity", "terms", "scope", "notes"];
const ROLES: readonly ClientUserRole[] = ["admin", "buyer", "viewer"];

const STATE_TONE: Record<ClientUserState, "success" | "neutral" | "danger"> = {
  active: "success",
  invited: "neutral",
  suspended: "danger",
};

export interface ClientScreenProps {
  client: ApiClient;
  id: string;
  /** The tab to open. Comes from the URL. */
  tab?: string;
  onTabChange?: (tab: string) => void;
  /** Injected by the application: the tariff lines belong to the referential. */
  loadTariff?: LoadTariff;
  /**
   * The internal-notes panel, injected by the application.
   *
   * §6.12's notes belong to `support`, and a feature may not import a sibling
   * feature — so the composition root passes the panel in. Absent when the
   * module is not installed, and the tab then does not exist: a tab that
   * renders nothing is worse than a missing one.
   */
  notesPanel?: (clientId: string) => ReactNode;
}

export function ClientScreen({
  client,
  id,
  tab,
  onTabChange,
  loadTariff,
  notesPanel,
}: ClientScreenProps) {
  const { t, i18n } = useTranslation(NAMESPACE);
  const api = useMemo(() => new ClientsApi(client), [client]);
  const toast = useToast();
  const action = useAction();

  const mayWrite = useCan("clients.client.write");
  const mayChangeAccess = useCan("clients.client.access");
  const mayReadUsers = useCan("clients.user.read");
  const mayWriteUsers = useCan("clients.user.write");

  const fetcher = useCallback(() => api.getClient(id), [api, id]);
  const record = useResource(fetcher);

  // The notes tab exists only when the application injected the panel. A tab
  // whose content is a module that is not installed is a dead end.
  const tabs: readonly TabId[] = notesPanel
    ? TAB_IDS
    : TAB_IDS.filter((entry) => entry !== "notes");
  const active: TabId = tabs.includes(tab as TabId) ? (tab as TabId) : "users";
  const setTab = (next: string) => onTabChange?.(next);

  if (record.loading) return <LoadingState label={t("client.loading")} />;
  const subject = record.data;
  if (!subject) {
    return (
      <ErrorState
        title={record.failed ? t("error.title") : t("client.notFound")}
        {...(record.failed ? { onRetry: record.reload, retryLabel: t("error.retry") } : {})}
      />
    );
  }

  return (
    <Page
      title={subject.company_name}
      back={<Link to={CLIENTS_PATHS.clients}>{t("client.back")}</Link>}
      eyebrow={`${subject.code}${subject.tax_id ? ` · ICE ${subject.tax_id}` : ""}`}
      actions={
        <>
          <StatusDot
            label={t(`client.status.${subject.portal_status}`)}
            tone={
              subject.portal_status === "active"
                ? "success"
                : subject.portal_status === "suspended"
                  ? "danger"
                  : "neutral"
            }
          />
          {mayChangeAccess ? (
            <Button
              variant={subject.portal_status === "suspended" ? "primary" : "secondary"}
              busy={action.isBusy("access")}
              busyLabel={t("client.suspend")}
              onClick={() =>
                void action.run("access", async () => {
                  try {
                    await api.setPortalStatus(
                      subject.id,
                      subject.portal_status === "suspended" ? "active" : "suspended",
                    );
                    record.reload();
                  } catch {
                    toast.show(t("error.save"), { tone: "danger" });
                  }
                })
              }
            >
              {subject.portal_status === "suspended" ? t("client.restore") : t("client.suspend")}
            </Button>
          ) : null}
        </>
      }
    >
      <div className="hardauto-client">
        <div className="hardauto-client__stack">
          <Tabs
            label={t("client.tab.users")}
            active={active}
            onSelect={setTab}
            items={tabs.map((entry) => ({ id: entry, label: t(`client.tab.${entry}`) }))}
          />

          {active === "users" ? (
            mayReadUsers ? (
              <UsersTab
                api={api}
                clientId={subject.id}
                mayWrite={mayWriteUsers}
                locale={i18n.language}
              />
            ) : null
          ) : null}

          {active === "identity" ? (
            <IdentityTab api={api} subject={subject} mayWrite={mayWrite} onSaved={record.reload} />
          ) : null}

          {active === "terms" ? (
            <TermsTab
              scaleCode={subject.price_scale_code}
              locale={i18n.language}
              {...(loadTariff ? { loadTariff } : {})}
            />
          ) : null}

          {active === "scope" ? (
            <ScopeTab api={api} clientId={subject.id} mayWrite={mayWrite} />
          ) : null}

          {/* The panel comes from the application, because §6.12's notes
              belong to `support` and a feature may not import a sibling. */}
          {active === "notes" ? notesPanel?.(subject.id) : null}
        </div>

        {/* Outside the tabs on purpose: see the note at the top of the file. */}
        <CreditPanel subject={subject} locale={i18n.language} />
      </div>
    </Page>
  );
}

/** Credit, seen from three angles. One fact, not three numbers. */
function CreditPanel({ subject, locale }: { subject: Client; locale: string }) {
  const { t } = useTranslation(NAMESPACE);

  return (
    <Card title={t("client.credit.title")} compactTitle>
      <div className="hardauto-credit">
        <Meter
          value={subject.credit_usage_pct}
          text={`${subject.credit_usage_pct} %`}
          label={t("client.credit.usage")}
          tone={creditTone(subject.credit_usage_pct, subject.is_over_credit_limit)}
        />
        <dl className="hardauto-credit__figures">
          <div className="hardauto-credit__figure">
            <dt>{t("client.credit.limit")}</dt>
            <dd>{money(subject.credit_limit, locale)}</dd>
          </div>
          <div className="hardauto-credit__figure" data-over={subject.is_over_credit_limit}>
            <dt>{t("client.credit.used")}</dt>
            <dd>{money(subject.credit_used, locale)}</dd>
          </div>
          <div className="hardauto-credit__figure">
            <dt>{t("client.credit.available")}</dt>
            <dd>{money(subject.credit_available, locale)}</dd>
          </div>
        </dl>
        <p className="hardauto-cell__muted">{t("client.credit.usedHint")}</p>
        <DefinitionList
          items={[
            {
              label: t("client.credit.scale"),
              value: subject.price_scale_code || t("client.credit.noScale"),
            },
          ]}
        />
      </div>
    </Card>
  );
}

/** Who may act for this customer. */
function UsersTab({
  api,
  clientId,
  mayWrite,
  locale,
}: {
  api: ClientsApi;
  clientId: string;
  mayWrite: boolean;
  locale: string;
}) {
  const { t } = useTranslation(NAMESPACE);
  const toast = useToast();
  const action = useAction();

  const fetcher = useCallback(() => api.listClientUsers(clientId), [api, clientId]);
  const users = useResource(fetcher);

  const [inviting, setInviting] = useState(false);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<ClientUserRole>("viewer");

  const columns: Column<ClientUser>[] = [
    {
      key: "contact",
      header: t("client.users.column.contact"),
      render: (row) => row.display_name || "—",
    },
    { key: "email", header: t("client.users.column.email"), render: (row) => row.email || "—" },
    {
      key: "role",
      header: t("client.users.column.role"),
      render: (row) =>
        mayWrite ? (
          <Select
            aria-label={t("client.users.column.role")}
            value={row.role}
            disabled={action.isBusy(row.id)}
            onChange={(event) =>
              void action.run(row.id, async () => {
                try {
                  await api.updateClientUser(row.id, {
                    role: event.target.value as ClientUserRole,
                  });
                  users.reload();
                } catch {
                  toast.show(t("error.save"), { tone: "danger" });
                }
              })
            }
          >
            {ROLES.map((entry) => (
              <option key={entry} value={entry}>
                {t(`client.role.${entry}`)}
              </option>
            ))}
          </Select>
        ) : (
          t(`client.role.${row.role}`)
        ),
    },
    {
      key: "scope",
      header: t("client.users.column.scope"),
      render: (row) =>
        // The EFFECTIVE scope, not what was typed: the first is a wish and the
        // second is what this person can actually reach.
        row.effective_site_codes.length === 0 ? (
          <span className="hardauto-cell__muted">{t("client.users.allSites")}</span>
        ) : (
          row.effective_site_codes.join(", ")
        ),
    },
    {
      key: "lastSeen",
      header: t("client.users.column.lastSeen"),
      // The fallback is explicit rather than `|| t(...)`: that worked only
      // because the old formatter returned the empty string for a missing
      // value, and it would have broken silently the day it stopped.
      render: (row) => moment(row.last_seen_at, locale, t("client.users.never")),
    },
    {
      key: "state",
      header: t("client.users.column.state"),
      render: (row) => (
        <StatusDot label={t(`client.state.${row.state}`)} tone={STATE_TONE[row.state]} />
      ),
    },
    {
      key: "actions",
      header: t("client.users.column.actions"),
      render: (row) =>
        mayWrite ? (
          <Button
            variant="ghost"
            size="sm"
            busy={action.isBusy(`state:${row.id}`)}
            busyLabel={t("client.users.suspend")}
            onClick={() =>
              void action.run(`state:${row.id}`, async () => {
                try {
                  await api.setClientUserState(
                    row.id,
                    row.state === "suspended" ? "active" : "suspended",
                  );
                  users.reload();
                } catch {
                  toast.show(t("error.save"), { tone: "danger" });
                }
              })
            }
          >
            {row.state === "suspended" ? t("client.users.activate") : t("client.users.suspend")}
          </Button>
        ) : null,
    },
  ];

  return (
    <Card
      title={t("client.users.title")}
      compactTitle
      flush
      actions={
        mayWrite ? (
          <Button variant="primary" onClick={() => setInviting(true)}>
            {t("client.invite.action")}
          </Button>
        ) : undefined
      }
    >
      <DataTable<ClientUser>
        caption={t("client.users.caption")}
        columns={columns}
        rows={users.data?.items ?? []}
        rowKey={(row) => row.id}
        loading={users.loading}
        loadingLabel={t("client.loading")}
        error={
          users.failed
            ? { title: t("error.title"), onRetry: users.reload, retryLabel: t("error.retry") }
            : undefined
        }
        empty={{
          title: t("client.users.empty.title"),
          description: t("client.users.empty.body"),
        }}
      />

      <Dialog
        open={inviting}
        onClose={() => setInviting(false)}
        title={t("client.invite.title")}
        closeLabel={t("client.invite.cancel")}
        footer={
          <>
            <Button onClick={() => setInviting(false)}>{t("client.invite.cancel")}</Button>
            <Button
              variant="primary"
              busy={action.isBusy("invite")}
              busyLabel={t("client.invite.send")}
              onClick={() =>
                void action.run("invite", async () => {
                  if (!email.trim()) return;
                  try {
                    await api.inviteClientUser(clientId, { email: email.trim(), role });
                    toast.show(t("client.invite.sent", { email: email.trim() }), {
                      tone: "success",
                    });
                    setEmail("");
                    setInviting(false);
                    users.reload();
                  } catch {
                    toast.show(t("error.save"), { tone: "danger" });
                  }
                })
              }
            >
              {t("client.invite.send")}
            </Button>
          </>
        }
      >
        <div className="hardauto-form">
          <Field label={t("client.invite.email")} required>
            {(props) => (
              <Input
                {...props}
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            )}
          </Field>
          <Field label={t("client.invite.role")} hint={t(`client.roleHint.${role}`)}>
            {(props) => (
              <Select
                {...props}
                value={role}
                onChange={(event) => setRole(event.target.value as ClientUserRole)}
              >
                {ROLES.map((entry) => (
                  <option key={entry} value={entry}>
                    {t(`client.role.${entry}`)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <p className="hardauto-cell__muted">{t("client.invite.scopeHint")}</p>
        </div>
      </Dialog>
    </Card>
  );
}

/** What the ERP knows, and what the portal adds. */
function IdentityTab({
  api,
  subject,
  mayWrite,
  onSaved,
}: {
  api: ClientsApi;
  subject: Client;
  mayWrite: boolean;
  onSaved: () => void;
}) {
  const { t, i18n } = useTranslation(NAMESPACE);
  const toast = useToast();
  const action = useAction();

  const [displayName, setDisplayName] = useState(subject.display_name);
  const [billingContact, setBillingContact] = useState(subject.billing_contact);
  const [internalNote, setInternalNote] = useState(subject.internal_note);

  return (
    <>
      <Card title={t("client.identity.title")} compactTitle>
        <DefinitionList
          items={[
            { label: t("client.identity.code"), value: <code>{subject.code}</code> },
            { label: t("client.identity.company"), value: subject.company_name },
            { label: t("client.identity.taxId"), value: subject.tax_id || "—" },
            { label: t("client.identity.city"), value: subject.city || "—" },
            { label: t("client.identity.address"), value: subject.address || "—" },
            {
              label: t("client.identity.created"),
              value: day(subject.created_at, i18n.language),
            },
          ]}
        />
      </Card>

      <Card title={t("client.identity.portalTitle")} compactTitle>
        <form
          className="hardauto-form"
          onSubmit={(event) => {
            event.preventDefault();
            void action.run("identity", async () => {
              try {
                await api.updateClient(subject.id, {
                  display_name: displayName,
                  billing_contact: billingContact,
                  internal_note: internalNote,
                });
                toast.show(t("client.identity.saved"), { tone: "success" });
                onSaved();
              } catch {
                toast.show(t("error.save"), { tone: "danger" });
              }
            });
          }}
        >
          <Field
            label={t("client.identity.displayName")}
            hint={t("client.identity.displayNameHint")}
          >
            {(props) => (
              <Input
                {...props}
                value={displayName}
                disabled={!mayWrite}
                onChange={(event) => setDisplayName(event.target.value)}
              />
            )}
          </Field>
          <Field label={t("client.identity.billingContact")}>
            {(props) => (
              <Input
                {...props}
                value={billingContact}
                disabled={!mayWrite}
                onChange={(event) => setBillingContact(event.target.value)}
              />
            )}
          </Field>
          <Field
            label={t("client.identity.internalNote")}
            hint={t("client.identity.internalNoteHint")}
          >
            {(props) => (
              <Textarea
                {...props}
                value={internalNote}
                disabled={!mayWrite}
                onChange={(event) => setInternalNote(event.target.value)}
              />
            )}
          </Field>
          {mayWrite ? (
            <div className="hardauto-form__actions">
              <Button
                type="submit"
                variant="primary"
                busy={action.isBusy("identity")}
                busyLabel={t("client.identity.save")}
              >
                {t("client.identity.save")}
              </Button>
            </div>
          ) : null}
        </form>
      </Card>
    </>
  );
}

/** The tariff lines this customer is priced under. */
function TermsTab({
  scaleCode,
  locale,
  loadTariff,
}: {
  scaleCode: string;
  locale: string;
  loadTariff?: LoadTariff;
}) {
  const { t } = useTranslation(NAMESPACE);

  const fetcher = useCallback(
    () => (scaleCode && loadTariff ? loadTariff(scaleCode) : Promise.resolve([])),
    [scaleCode, loadTariff],
  );
  const lines = useResource(fetcher);

  if (!scaleCode) {
    return (
      <Card title={t("client.terms.title")} compactTitle>
        <p className="hardauto-cell__muted">{t("client.terms.noScale")}</p>
      </Card>
    );
  }

  const columns: Column<TariffLine>[] = [
    {
      key: "family",
      header: t("client.terms.column.family"),
      render: (row) =>
        row.familyCode ?? <span className="hardauto-cell__muted">{t("client.terms.all")}</span>,
    },
    {
      key: "article",
      header: t("client.terms.column.article"),
      render: (row) =>
        row.articleReference ? (
          <span className="hardauto-ref">{row.articleReference}</span>
        ) : (
          <span className="hardauto-cell__muted">{t("client.terms.all")}</span>
        ),
    },
    {
      key: "site",
      header: t("client.terms.column.site"),
      render: (row) =>
        row.siteCode ?? <span className="hardauto-cell__muted">{t("client.terms.allSites")}</span>,
    },
    {
      key: "discount",
      header: t("client.terms.column.discount"),
      render: (row) => <span className="hardauto-num">{percent(row.discountPct, locale)}</span>,
    },
    {
      key: "fixed",
      header: t("client.terms.column.fixed"),
      render: (row) => (
        <span className="hardauto-num">{row.fixedPrice ? money(row.fixedPrice, locale) : "—"}</span>
      ),
    },
    {
      key: "validity",
      header: t("client.terms.column.validity"),
      render: (row) =>
        row.validFrom || row.validTo ? (
          `${row.validFrom ?? "…"} → ${row.validTo ?? "…"}`
        ) : (
          <span className="hardauto-cell__muted">{t("client.terms.openEnded")}</span>
        ),
    },
    {
      key: "visible",
      header: t("client.terms.column.visible"),
      render: (row) => (row.visibleOnPortal ? t("client.terms.yes") : t("client.terms.no")),
    },
  ];

  return (
    <Card title={t("client.terms.title")} description={t("client.terms.intro")} compactTitle flush>
      <DataTable<TariffLine>
        caption={t("client.terms.caption")}
        columns={columns}
        rows={lines.data ?? []}
        rowKey={(row) => row.id}
        loading={lines.loading}
        loadingLabel={t("client.loading")}
        error={
          lines.failed
            ? { title: t("error.title"), onRetry: lines.reload, retryLabel: t("error.retry") }
            : undefined
        }
        empty={{ title: t("client.terms.empty.title"), description: t("client.terms.empty.body") }}
      />
    </Card>
  );
}

/** Which depots this customer is allowed. */
function ScopeTab({
  api,
  clientId,
  mayWrite,
}: {
  api: ClientsApi;
  clientId: string;
  mayWrite: boolean;
}) {
  const { t } = useTranslation(NAMESPACE);
  const toast = useToast();
  const action = useAction();

  const fetcher = useCallback(() => api.getScope(clientId), [api, clientId]);
  const scope = useResource(fetcher);

  // Local until saved: the screen is a set of toggles and the API replaces the
  // whole set, so submitting the result is what stops two operators racing.
  const [draft, setDraft] = useState<ReadonlySet<string> | null>(null);
  const granted = draft ?? new Set(scope.data?.granted ?? []);

  if (scope.loading) return <LoadingState label={t("client.loading")} />;
  if (!scope.data) {
    return (
      <ErrorState title={t("error.title")} onRetry={scope.reload} retryLabel={t("error.retry")} />
    );
  }

  return (
    <Card title={t("client.scope.title")} description={t("client.scope.intro")} compactTitle>
      {granted.size === 0 ? (
        <p className="hardauto-scope__note">{t("client.scope.unrestricted")}</p>
      ) : null}
      <div className="hardauto-scope">
        {scope.data.items.map((site) => (
          <Checkbox
            key={site.code}
            label={`${site.name} (${site.code})`}
            checked={granted.has(site.code)}
            disabled={!mayWrite}
            onChange={(event) => {
              const next = new Set(granted);
              if (event.target.checked) next.add(site.code);
              else next.delete(site.code);
              setDraft(next);
            }}
          />
        ))}
      </div>
      {mayWrite ? (
        <Button
          variant="primary"
          busy={action.isBusy("scope")}
          busyLabel={t("client.scope.save")}
          onClick={() =>
            void action.run("scope", async () => {
              try {
                await api.setScope(clientId, [...granted]);
                toast.show(t("client.scope.saved"), { tone: "success" });
                setDraft(null);
                scope.reload();
              } catch {
                toast.show(t("error.save"), { tone: "danger" });
              }
            })
          }
        >
          {t("client.scope.save")}
        </Button>
      ) : null}
    </Card>
  );
}
